import { BEIGE_600, GRAY_900 } from 'constants/styling/theme'
import React, { Dispatch, SetStateAction, useCallback, useEffect } from 'react'

import { BorderBoxWrapper } from 'components/common/BorderBoxWrapper'
import Collapse from '@mui/material/Collapse'
import { ImageBoxButton } from 'components/common/ImageBoxButton'
import { MUIDivider } from 'components/common/MUIDivider'
import { MUISwitch } from 'components/common/MUISwitch'
import { SkyReplacementOption } from 'models/purchaseFlow/AIEditingDTO'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import sky_clear_thumb from 'assets/img/ai_settings/clear.webp'
import sky_cloudy_thumb from 'assets/img/ai_settings/cloudy.webp'
import sky_low_cloud_thumb from 'assets/img/ai_settings/low_cloud.webp'
import { useTranslation } from 'react-i18next'

const skyReplacementImages = {
  [SkyReplacementOption.NO_CLOUD]: sky_clear_thumb,
  [SkyReplacementOption.LOW_CLOUD]: sky_low_cloud_thumb,
  [SkyReplacementOption.CLOUDY]: sky_cloudy_thumb,
}

/**
 * @interface Props for the AIEditingProductSettings component.
 */
interface Props {
  /** Whether is sky replacement enabled */
  isSkyReplacementEnabled: boolean
  /** Function to set the sky replacement enabled state */
  setIsSkyReplacementEnabled: Dispatch<SetStateAction<boolean>>
  /** The currently selected sky replacement option */
  selectedSkyReplacement?: SkyReplacementOption | null
  /** Function to set the selected sky replacement option */
  setSelectedSkyReplacement: Dispatch<SetStateAction<SkyReplacementOption | null>>
  /** Function called after sky replacement settings are updated */
  afterSkyReplacementSet: (params: {
    skyReplacement?: boolean,
    skyType?: string,
  }) => void
}

/**
 * @component
 * AIEditingProductSettings provides UI controls for AI-based photo editing settings.
 * 
 * @example
 * <AIEditingProductSettings />
 */
export const AIEditingProductSettings: React.FC<Props> = ({
  isSkyReplacementEnabled,
  selectedSkyReplacement,
  setSelectedSkyReplacement,
  setIsSkyReplacementEnabled,
  afterSkyReplacementSet,
}) => {

  const { t } = useTranslation(['ai_settings'])

  const getSkyReplacementImage = (option: SkyReplacementOption) => {
    return skyReplacementImages[option]
  }

  /** Function is only used for CLICK_AI_EDITING_OPTIONS analytics. **/
  const getSkyType = useCallback((option: SkyReplacementOption) => {
    switch (option) {
      case SkyReplacementOption.NO_CLOUD:
        return 'No clouds'
      case SkyReplacementOption.LOW_CLOUD:
        return 'Low clouds'
      case SkyReplacementOption.CLOUDY:
        return 'Lots of clouds'
      default:
        return 'Not selected'
    }
  }, [])

  useEffect(() => {
    if (!isSkyReplacementEnabled && !!selectedSkyReplacement) {
      setSelectedSkyReplacement(null)
      afterSkyReplacementSet({})
    }
  }, [isSkyReplacementEnabled, selectedSkyReplacement, setSelectedSkyReplacement, afterSkyReplacementSet])

  return (
    <Stack gap={1} marginTop="2.4rem">

      <Typography variant="text-md" fontWeight={600} color={GRAY_900}>
        {t('title')}
      </Typography>

      <BorderBoxWrapper
        padding={2}
        elevation="none"
        borderColor={BEIGE_600}
      >

        <MUISwitch
          label={
            <Typography variant="text-md" fontWeight={600} color={GRAY_900}>
              {t('sky_replacement_title')}
            </Typography>
          }
          labelPlacement="end"
          checked={isSkyReplacementEnabled}
          onChange={() => setIsSkyReplacementEnabled(prevState => !prevState)}
        />

        {/** Sky replacement options */}
        <Collapse in={isSkyReplacementEnabled}>

          <MUIDivider margin={16} />

          <Stack direction="row" gap={1.5}>

            {Object.values(SkyReplacementOption).map(option =>
              <ImageBoxButton
                key={option}
                altText={option}
                label={t(`sky_replacement_options.${option}`)}
                imageUrl={getSkyReplacementImage(option)}
                selected={selectedSkyReplacement === option}
                onClick={() => {
                  setSelectedSkyReplacement(option)
                  afterSkyReplacementSet({
                    skyReplacement: isSkyReplacementEnabled,
                    skyType: getSkyType(option),
                  })
                }}
              />
            )}

          </Stack>

        </Collapse>

      </BorderBoxWrapper>

    </Stack>
  )
}
